/**
 * System Info — mirrors apps/experts/public/admin/system-info.html. One GET
 * /api/admin/system-info returns per-dimension sections (app, container,
 * database, knowledge, loop, dependencies, crons); each section can come back
 * as an `{ error }` stub, which we render per-tile rather than failing the page.
 */
import type { ReactNode } from 'react';
import {
  AdminError,
  AdminLoading,
  AdminPageHeader,
  Mono,
  Panel,
  StatusDot,
  Table,
  Td,
  Th,
  toneForStatus,
  Tr,
} from '../../components/primitives';
import { useSystemInfo } from '../../data-access/useAdmin';
import { absTime, isErrorSection, relTime } from '../../lib/format';

function DefList({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 font-inter text-sm">
      {rows.map(([k, v]) => (
        <FragmentRow key={k} label={k} value={v} />
      ))}
    </dl>
  );
}

function FragmentRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="text-content-secondary">{label}</dt>
      <dd className="break-words text-content-primary">{value}</dd>
    </>
  );
}

export function SystemInfoPage() {
  const { data, isPending, isError, error, refetch, isFetching } =
    useSystemInfo();

  return (
    <div>
      <AdminPageHeader
        actions={
          <button
            className="rounded-md border border-border-subtle px-3 py-1.5 font-inter text-content-secondary text-sm hover:bg-surface-background"
            onClick={() => void refetch()}
            type="button"
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        }
        subtitle={
          data
            ? `Generated ${relTime(data.generated_at)}`
            : 'Operational snapshot'
        }
        title="System Info"
      />

      {isPending ? <AdminLoading /> : null}
      {isError ? (
        <AdminError
          message={`Failed to load: ${(error as Error)?.message ?? 'unknown'}`}
        />
      ) : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="App">
            {isErrorSection(data.app) ? (
              <AdminError message={data.app.error} />
            ) : (
              <DefList
                rows={[
                  ['Commit', <Mono key="c">{data.app.commit ?? '—'}</Mono>],
                  ['Environment', data.app.environment],
                  ['Uptime', `${data.app.uptime_seconds}s`],
                  [
                    'Image tag',
                    <Mono key="i">{data.app.imageTag ?? '—'}</Mono>,
                  ],
                ]}
              />
            )}
          </Panel>

          <Panel title="Container">
            {isErrorSection(data.container) ? (
              <AdminError message={data.container.error} />
            ) : (
              <DefList
                rows={[
                  [
                    'Revision',
                    <Mono key="r">{data.container.revision ?? '—'}</Mono>,
                  ],
                  ['Replicas', data.container.replica_count ?? '—'],
                  ['App name', data.container.app_name ?? '—'],
                  ['Started', relTime(data.container.started_at)],
                ]}
              />
            )}
          </Panel>

          <Panel title="Database">
            {isErrorSection(data.database) ? (
              <AdminError message={data.database.error} />
            ) : (
              <DefList
                rows={[
                  [
                    'PG version',
                    (data.database.pg_version ?? '')
                      .split(' ')
                      .slice(0, 2)
                      .join(' ') || '—',
                  ],
                  ['Connections', data.database.connection_count],
                  ['Knowledge chunks', data.database.knowledge_chunks],
                  ['Oldest chunk', absTime(data.database.oldest_chunk_at)],
                  ['Newest chunk', absTime(data.database.newest_chunk_at)],
                ]}
              />
            )}
          </Panel>

          <Panel title="Knowledge & loop">
            <div className="flex flex-col gap-3">
              {isErrorSection(data.knowledge) ? (
                <AdminError message={data.knowledge.error} />
              ) : (
                <DefList
                  rows={[
                    ['KB articles', data.knowledge.article_count],
                    ['Last KB sync', relTime(data.knowledge.last_sync_at)],
                  ]}
                />
              )}
              {isErrorSection(data.loop) ? (
                <AdminError message={data.loop.error} />
              ) : (
                <DefList
                  rows={[
                    ['Loop score', data.loop.score ?? '—'],
                    ['Loop last run', relTime(data.loop.last_run_at)],
                    ['Suite size', data.loop.suite_size ?? '—'],
                  ]}
                />
              )}
            </div>
          </Panel>

          <Panel title="Dependencies">
            {isErrorSection(data.dependencies) ? (
              <AdminError message={data.dependencies.error} />
            ) : (
              <Table
                head={
                  <>
                    <Th>Dependency</Th>
                    <Th>Status</Th>
                    <Th>Latency</Th>
                  </>
                }
              >
                {Object.entries(data.dependencies).map(([name, dep]) => (
                  <Tr key={name}>
                    <Td>
                      <Mono>{name}</Mono>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={toneForStatus(dep.status)} />
                        {dep.status}
                        {dep.note ? ` (${dep.note})` : ''}
                      </span>
                    </Td>
                    <Td>
                      {dep.latency_ms != null ? `${dep.latency_ms}ms` : '—'}
                    </Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>

          <Panel title="Cron jobs">
            {isErrorSection(data.crons) ? (
              <AdminError message={data.crons.error} />
            ) : (
              <Table
                head={
                  <>
                    <Th>Job</Th>
                    <Th>Schedule</Th>
                    <Th>Last</Th>
                    <Th>Next</Th>
                  </>
                }
              >
                {data.crons.map((cron) => (
                  <Tr key={cron.name}>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <StatusDot tone={toneForStatus(cron.last_status)} />
                        <Mono title={cron.name}>
                          {cron.name
                            .replace('caj-andavo-labs-', '')
                            .replace('-dev', '')}
                        </Mono>
                      </span>
                    </Td>
                    <Td>
                      <Mono>{cron.schedule}</Mono>
                    </Td>
                    <Td>{relTime(cron.last_run_at)}</Td>
                    <Td>{relTime(cron.next_run_at)}</Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Panel>
        </div>
      ) : null}
    </div>
  );
}

export default SystemInfoPage;
